import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import openai from '../../../services/openaiClient';
import { Building2, Sparkles, MapPin, DollarSign, TrendingUp, FileBarChart } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const PropertiesActionsModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('analysis');
  const [loading, setLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [location, setLocation] = useState('');

  const analyzeProperties = async (analysisType) => {
    setLoading(true);
    try {
      const prompts = {
        performance: 'قم بتحليل أداء العقارات من حيث معدلات الإشغال والإيرادات وقدم توصيات للتحسين',
        pricing: 'قم بتحليل استراتيجية التسعير الحالية للعقارات وقدم توصيات لتحسين الأسعار',
        market: 'قم بتحليل وضع العقارات في السوق الحالي وحدد الفرص والتهديدات',
        location: `قم بتحليل أداء العقارات في منطقة ${location} وقدم رؤى حول السوق المحلي`,
        type: `قم بتحليل أداء عقارات ${propertyType} وقدم توصيات استراتيجية`
      };

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: 'أنت محلل عقاري متخصص في تقييم أداء العقارات وتقديم رؤى استراتيجية.'
          },
          {
            role: 'user',
            content: prompts?.[analysisType]
          }
        ],
        max_tokens: 500,
        temperature: 0.7
      });

      setAiAnalysis(response?.choices?.[0]?.message?.content);
    } catch (error) {
      console.error('Error analyzing properties:', error);
      
      // Handle specific error types
      if (error?.status === 429 || error?.message?.includes('quota') || error?.message?.includes('429')) {
        setAiAnalysis('⚠️ تم تجاوز حد الاستخدام لخدمة التحليل الذكي. يرجى التواصل مع مسؤول النظام للتحقق من خطة الاشتراك وتفاصيل الفوترة.');
      } else if (error?.status === 401 || error?.message?.includes('authentication')) {
        setAiAnalysis('⚠️ خطأ في المصادقة. يرجى التحقق من إعدادات API.');
      } else if (error?.status === 500 || error?.status === 503) {
        setAiAnalysis('⚠️ خدمة التحليل الذكي غير متاحة حالياً. يرجى المحاولة مرة أخرى لاحقاً.');
      } else {
        setAiAnalysis('⚠️ حدث خطأ في التحليل. يرجى المحاولة مرة أخرى أو التواصل مع الدعم الفني.');
      }
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'analysis', label: 'تحليل ذكي', icon: Sparkles },
    { id: 'reports', label: 'تقارير العقارات', icon: FileBarChart },
    { id: 'metrics', label: 'المقاييس', icon: TrendingUp }
  ];

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="تقارير العقارات - تحليلات متقدمة" 
      size="large"
      icon={Building2}
      iconColor="text-primary"
      footer={null}
    >
      <div className="flex border-b border-border mb-6">
        {tabs?.map((tab) => {
          const Icon = tab?.icon;
          return (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
                activeTab === tab?.id
                  ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab?.label}
            </button>
          );
        })}
      </div>
      {activeTab === 'analysis' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button
              onClick={() => analyzeProperties('performance')}
              disabled={loading}
              className="h-24 flex flex-col items-center justify-center gap-2"
            >
              <Building2 className="w-6 h-6" />
              <span>تحليل أداء العقارات</span>
            </Button>
            <Button
              onClick={() => analyzeProperties('pricing')}
              disabled={loading}
              className="h-24 flex flex-col items-center justify-center gap-2"
            >
              <DollarSign className="w-6 h-6" />
              <span>تحليل استراتيجية التسعير</span>
            </Button>
            <Button
              onClick={() => analyzeProperties('market')}
              disabled={loading}
              className="h-24 flex flex-col items-center justify-center gap-2"
            >
              <TrendingUp className="w-6 h-6" />
              <span>تحليل وضع السوق</span>
            </Button>
            <div className="space-y-2">
              <Input
                placeholder="المنطقة (مثال: دبي، الشارقة)"
                value={location}
                onChange={(e) => setLocation(e?.target?.value)}
              />
              <Button
                onClick={() => analyzeProperties('location')}
                disabled={loading || !location}
                className="w-full"
              >
                <MapPin className="w-4 h-4 ml-2" />
                تحليل حسب المنطقة
              </Button>
            </div>
          </div>

          <div className="flex gap-2">
            <Input
              placeholder="نوع العقار (مثال: شقق، فلل، مكاتب)"
              value={propertyType}
              onChange={(e) => setPropertyType(e?.target?.value)}
              className="flex-1"
            />
            <Button
              onClick={() => analyzeProperties('type')}
              disabled={loading || !propertyType}
            >
              <Sparkles className="w-4 h-4 ml-2" />
              تحليل
            </Button>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="mr-3 text-muted-foreground">جاري التحليل...</span>
            </div>
          )}

          {aiAnalysis && !loading && (
            <div className="bg-success/5 border border-success/20 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-success" />
                <h3 className="font-semibold text-foreground">نتائج التحليل</h3>
              </div>
              <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{aiAnalysis}</p>
            </div>
          )}
        </div>
      )}
      {activeTab === 'reports' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { title: 'تقرير الإشغال', desc: 'معدلات الإشغال حسب نوع العقار' },
              { title: 'تقرير الإيرادات', desc: 'تحليل الإيرادات من العقارات' },
              { title: 'تقرير المناطق', desc: 'أداء العقارات حسب المنطقة' },
              { title: 'تقرير الصيانة', desc: 'تكاليف وجدولة الصيانة' }
            ]?.map((report, idx) => (
              <div key={idx} className="border border-border rounded-lg p-4 hover:border-success transition-colors cursor-pointer">
                <h4 className="font-medium text-foreground mb-1">{report?.title}</h4>
                <p className="text-xs text-muted-foreground mb-3">{report?.desc}</p>
                <Button size="sm" variant="outline">عرض التقرير</Button>
              </div>
            ))}
          </div>
        </div>
      )}
      {activeTab === 'metrics' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { label: 'معدل الإشغال', value: '٨٥٪', change: '+٥٪', positive: true },
              { label: 'متوسط الإيجار', value: '٤,٥٠٠ د.إ', change: '+٨٪', positive: true },
              { label: 'معدل التجديد', value: '٧٢٪', change: '-٣٪', positive: false },
              { label: 'وقت الإشغال', value: '١٢ يوم', change: '-٤ أيام', positive: true },
              { label: 'رضا المستأجرين', value: '٤.٦/٥', change: '+٠.٣', positive: true },
              { label: 'تكلفة الصيانة', value: '٣٥٠ د.إ', change: '+١٥٪', positive: false }
            ]?.map((metric, idx) => (
              <div key={idx} className="border border-border rounded-lg p-4">
                <div className="text-xs text-muted-foreground mb-1">{metric?.label}</div>
                <div className="text-2xl font-bold text-foreground mb-1">{metric?.value}</div>
                <div className={`text-xs ${metric?.positive ? 'text-success' : 'text-destructive'}`}>
                  {metric?.change}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default PropertiesActionsModal;